import { log } from '@/utils/log';
import { ref } from 'vue';

export interface ServiceWorkerState {
    isSupported: boolean;
    isRegistered: boolean;
    isUpdateAvailable: boolean;
    isOfflineReady: boolean;
}

const state = ref<ServiceWorkerState>({
    isSupported: 'serviceWorker' in navigator,
    isRegistered: false,
    isUpdateAvailable: false,
    isOfflineReady: false,
});

/**
 * Composable voor Service Worker management
 */
export function useServiceWorker() {
    /**
     * Registreer de service worker
     */
    const register = async () => {
        if (!state.value.isSupported) {
            log('Service Worker not supported in this browser');
            return;
        }

        // Skip service worker registration in development mode
        if (import.meta.env.DEV) {
            log('Service Worker registration skipped in development mode');
            return;
        }

        try {
            // Registreer de service worker (only in production)
            const swPath = '/service-worker.js';

            const registration = await navigator.serviceWorker.register(swPath, {
                type: 'module',
                scope: '/',
            });

            log('Service worker registered successfully', registration);
            state.value.isRegistered = true;

            // Check if there's a waiting service worker
            if (registration.waiting) {
                log('Service worker waiting to activate');
                state.value.isUpdateAvailable = true;
            }

            // Listen for new service worker
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            log('New service worker available');
                            state.value.isUpdateAvailable = true;
                        }
                    });
                }
            });

            // Check if service worker is controlling the page
            if (navigator.serviceWorker.controller) {
                log('Service worker is controlling the page');
                state.value.isOfflineReady = true;
            }

            // Listen for controller change (when new SW takes over)
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                log('Service worker controller changed');
                state.value.isOfflineReady = true;
            });

            // Check for updates periodically (every hour)
            setInterval(
                () => {
                    registration.update().catch((error) => {
                        log('Service worker update check failed:', error);
                    });
                },
                60 * 60 * 1000,
            );
        } catch (error) {
            log('Service worker registration failed:', error);
        }
    };

    /**
     * Update naar nieuwe service worker versie
     */
    const update = async () => {
        if (!state.value.isSupported) {
            return Promise.reject(new Error('Service Worker not supported'));
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) {
                throw new Error('No service worker registration found');
            }

            if (!registration.waiting) {
                log('No waiting service worker found');
                return Promise.resolve();
            }

            // Create a promise that resolves when the new SW takes control
            const controllerChangePromise = new Promise<void>((resolve) => {
                navigator.serviceWorker.addEventListener(
                    'controllerchange',
                    () => {
                        log('Controller changed, new service worker is now active');
                        resolve();
                    },
                    { once: true },
                );
            });

            // Tell the waiting service worker to skip waiting
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            log('Sent skip waiting message to service worker');

            // Wait for the controller change event
            await controllerChangePromise;
        } catch (error) {
            log('Failed to update service worker:', error);
            throw error;
        }
    };

    /**
     * Unregister de service worker (voor development/debugging)
     */
    const unregister = async () => {
        if (!state.value.isSupported) {
            return;
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                await registration.unregister();
                state.value.isRegistered = false;
                log('Service worker unregistered');
            }
        } catch (error) {
            log('Failed to unregister service worker:', error);
        }
    };

    /**
     * Notify service worker dat er een queue item is toegevoegd
     */
    const notifyQueueItemAdded = () => {
        if (state.value.isRegistered && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'QUEUE_ITEM_ADDED' });
            log('Notified service worker about queue item');
        }
    };

    /**
     * Check of er een update beschikbaar is
     */
    const checkForUpdate = async () => {
        if (!state.value.isSupported) {
            return;
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                await registration.update();
            }
        } catch (error) {
            log('Failed to check for updates:', error);
        }
    };

    return {
        // State
        state,

        // Methods
        register,
        update,
        unregister,
        notifyQueueItemAdded,
        checkForUpdate,
    };
}
